import { useEffect, useState } from "react";
import { logo } from "../../asset";
import { schoolName } from "../../util/Constants";
import StepsBar, { StepperActions } from "../../components/StepsBar";
import { Student } from "../../types/studentType";
import StudentApis from "../../api/studentApis";
import { useParams } from "react-router-dom";
import StudentProfileInfo from "./steps/StudentPersonalInfo";
import StudentFamilyDetails from "./steps/StudentFamilyDetails";
import StudentMedicalDetails from "./steps/StudentMedicalDetails";
import StudentDocumentDetails from "./steps/StudentDocumentDetails";
import StudentOtherDetails from "./steps/StudentOtherDetails";
import { IS_LIVE } from "../../util/config/env";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { getSchoolBranchByCode } from "../../features/school-branch-controller/schoolBranchSlice";

const admissionSections = [
  { id: 1, name: "Student Profile" },
  { id: 2, name: "Family Details" },
  ...(IS_LIVE ? [] : [{ id: 3, name: "Medical Details" }]),
  { id: 4, name: "Documents" },
  { id: 5, name: "Other Details" },
];

export default function AdmissionFormLink() {
  const { id } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<Student>>({});
  const [loading, setLoading] = useState(true);

  const { branchCode } = useParams();

  const dispatch = useDispatch<AppDispatch>();
  const { branchDetails } = useSelector(
    (state: RootState) => state.schoolBranch,
  );

  useEffect(() => {
    dispatch(getSchoolBranchByCode());
  }, [dispatch, branchCode]);

  useEffect(() => {
    if (!id) return;

    StudentApis.getStudentById(Number(id)).then((res) => {
      if (res?.status === 1) {
        setFormData(res.data);
      }
      setLoading(false);
    });
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    let newValue: any = value;

    if (type === "radio") {
      if (value === "true") newValue = true;
      else if (value === "false") newValue = false;
    }

    if (name === "admissionYear") {
      newValue = Number(value);
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async () => {
    await StudentApis.updateStudent(formData as Student);
  };

  const steps = [
    <StudentProfileInfo data={formData} onChange={handleChange} />,
    <StudentFamilyDetails data={formData} onChange={handleChange} />,
    ...(!IS_LIVE
      ? [<StudentMedicalDetails data={formData} onChange={handleChange} />]
      : []),
    <StudentDocumentDetails data={formData} setFormData={setFormData} />,
    <StudentOtherDetails data={formData} onChange={handleChange} />,
  ];

  const isFirst = currentStep === 1;
  const isLast = currentStep === steps.length;

  const validateStep = (step: number) => {
    if (step === 1) {
      return (
        !!formData.fullName &&
        !!formData.dateOfBirth &&
        !!formData.gender &&
        !!formData.academicCycleId
      );
    }

    return true;
  };

  if (loading) return <p className="text-center mt-10">Loading...</p>;

  return (
    <main className="min-h-screen bg-background-100 p-5">
      <section className="max-w-3xl mx-auto flex flex-col gap-6">
        <header className="text-center justify-center items-center flex flex-col gap-2">
          <img
            src={branchDetails?.schoolBranchIcon}
            alt="logo"
            className="h-16"
          />
          <h3 className="text-2xl font-semibold">
            {branchDetails?.schoolBranchName}
          </h3>
          <p className="text-sm text-neutral-400">
            Please complete the admission form
          </p>
        </header>

        <div className="bg-background-100 rounded-xl pt-2 px-10">
          <StepsBar
            current={currentStep}
            sections={admissionSections}
            onStepClick={setCurrentStep}
          />
        </div>

        <div className="bg-white flex flex-col gap-6 rounded-xl p-6 shadow">
          {steps[currentStep - 1]}
          <StepperActions
            isFirst={isFirst}
            isLast={isLast}
            isNextDisabled={!validateStep(currentStep)}
            onBack={() => setCurrentStep((p) => p - 1)}
            onNext={() => setCurrentStep((p) => p + 1)}
            onSubmit={handleSubmit}
          />
        </div>
      </section>
    </main>
  );
}
